import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';

// Real chat state manager using backend Socket.IO. Falls back to local storage for UI state only.
const ChatContext = createContext(null);

// Helper to build a stable DM room id for two users
function dmRoomId(a, b) {
  const s1 = String(a || '').trim();
  const s2 = String(b || '').trim();
  if (!s1 || !s2) return null;
  return ['dm', ...[s1, s2].sort()].join(':');
}

const initialState = {
  friends: [],
  chats: {}, // by friendId: { messages: [{id, from, text, ts}], unread, minimized, open, typing, roomId }
  openOrder: [],
  notifDot: false,
};

const LS_KEY = 'chat_ui_state_v1';

function loadState() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw);
    // Do not restore friends or chats from local storage to avoid any mock/local data
    const { friends: _f, chats: _c, ...rest } = parsed || {};
    return { ...initialState, ...rest };
  } catch (_) {
    return initialState;
  }
}

function saveState(state) {
  try {
    // Persist only UI flags; never persist friends or chats to prevent mock/local data from appearing
    const { friends: _f, chats: _c, ...uiOnly } = state || {};
    localStorage.setItem(LS_KEY, JSON.stringify(uiOnly));
  } catch (_) {}
}

export function ChatProvider({ children }) {
  // --- REST helpers for persistence ---
  const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
  const getToken = () => {
    try {
      return (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null) || '';
    } catch (_) { return ''; }
  };
  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getToken()}`,
  });
  const persistMessage = async ({ receiverId, text }) => {
    try {
      await fetch(`${API_BASE}/chat/messages`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ receiverId, receiverType: 'user', content: text, contentType: 'text', status: 'sent' })
      });
    } catch (_) {}
  };
  const fetchPersistedHistory = async ({ userId, peerId, limit = 50 }) => {
    try {
      const url = new URL(`${API_BASE}/chat/history/${encodeURIComponent(String(userId))}`, window.location.origin);
      url.searchParams.set('peerId', String(peerId));
      url.searchParams.set('limit', String(limit));
      const res = await fetch(url.toString(), { headers: authHeaders() });
      if (!res.ok) return null;
      const data = await res.json();
      return Array.isArray(data?.items) ? data.items : [];
    } catch (_) { return null; }
  };
  const [state, setState] = useState(loadState);
  const timersRef = useRef({});

  useEffect(() => {
    saveState(state);
  }, [state]);

  // Socket connections per DM room
  const socketsRef = useRef({}); // friendId -> socket

  // Reactive currentUserId that updates when token/user in storage changes
  const computeCurrentUserId = () => {
    const decodeJwt = (token) => {
      try {
        const base64 = token.split('.')[1];
        const payload = JSON.parse(atob(base64.replace(/-/g, '+').replace(/_/g, '/')));
        return payload || {};
      } catch (_) {
        return {};
      }
    };
    const resolveFromUserObj = () => {
      try {
        const raw = (typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('user') : null);
        if (!raw) return null;
        const u = JSON.parse(raw);
        const id = u?.id || u?._id || u?.userId || u?.uid || u?.username || null;
        return id ? String(id) : null;
      } catch (_) {
        return null;
      }
    };
    const resolveFromToken = () => {
      try {
        const tok = (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null);
        if (!tok) return null;
        const p = decodeJwt(tok);
        // Prefer explicit id fields over generic sub to match friends API ids
        let id = p?.userId || p?.id || p?._id || p?.uid || p?.sub || null;
        if (!id) return null;
        id = String(id);
        // If sub looks like an email or URL, treat as invalid for chat id
        if (/@|\s/.test(id) || /^https?:\/\//i.test(id)) {
          const alt = p?.userId || p?.id || p?._id || p?.uid || null;
          if (alt) return String(alt);
          return null;
        }
        return id;
      } catch (_) {
        return null;
      }
    };
    const fromUser = resolveFromUserObj();
    if (fromUser) return fromUser;
    const fromToken = resolveFromToken();
    if (fromToken) return fromToken;
    if (process.env.NODE_ENV !== 'production') {
      try { console.warn('[Chat] currentUserId fallback to "me" (no user or token id found)'); } catch (_) {}
    }
    return 'me';
  };
  const [currentUserId, setCurrentUserId] = useState(computeCurrentUserId);

  const chatServerUrl = useMemo(() => {
    // Resolve Socket.IO server URL robustly:
    // 1) REACT_APP_SOCKET_URL (highest priority)
    // 2) REACT_APP_CHAT_URL (legacy)
    // 3) Derived from REACT_APP_API_BASE_URL by stripping trailing /api
    // 4) In localhost dev, default to http(s)://localhost:5001 (main backend server includes chat handler)
    // 5) Fallback to window.location.origin
    const envUrl = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_CHAT_URL;
    if (envUrl) return envUrl;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (apiBase) {
      try {
        return String(apiBase).replace(/\/+$/, '').replace(/\/api$/, '');
      } catch (_) {}
    }
    if (typeof window !== 'undefined') {
      try {
        const { protocol, hostname, port } = window.location || {};
        // Prefer backend port 5001 when developing on localhost
        if (/^(localhost|127\.|0\.0\.0\.0)$/i.test(String(hostname))) {
          return `${protocol}//${hostname}:5001`;
        }
        // Otherwise same-origin
        const derived = `${protocol}//${hostname}${port ? (':' + port) : ''}`;
        if (process.env.NODE_ENV === 'production') {
          // Helpful hint in prod builds when no explicit socket URL was configured
          try { console.warn('[Chat] Using same-origin for Socket.IO. Consider setting REACT_APP_SOCKET_URL to your backend URL if chat is on a different host. Resolved:', derived); } catch (_) {}
        }
        return derived;
      } catch (_) {}
    }
    return '/';
  }, []);

  // Watch storage/auth changes to update currentUserId without full reload
  useEffect(() => {
    let disposed = false;
    const check = () => {
      if (disposed) return;
      try {
        const next = computeCurrentUserId();
        setCurrentUserId((prev) => (prev !== next ? next : prev));
      } catch (_) {}
    };
    const onStorage = () => check();
    const onFocus = () => check();
    try { window.addEventListener('storage', onStorage); } catch {}
    try { window.addEventListener('focus', onFocus); } catch {}
    const interval = setInterval(check, 2000);
    return () => {
      disposed = true;
      clearInterval(interval);
      try { window.removeEventListener('storage', onStorage); } catch {}
      try { window.removeEventListener('focus', onFocus); } catch {}
    };
  }, []);

  // Dedicated global presence socket to reflect login-based online status
  const presenceSocketRef = useRef(null);
  useEffect(() => {
    // Connect a lightweight presence socket once per user
    if (!currentUserId || presenceSocketRef.current) return;
    try {
      const s = io(chatServerUrl, {
        transports: ['websocket', 'polling'],
        auth: { username: String(currentUserId), groupId: 'presence' },
        query: { groupId: 'presence' },
        path: process.env.REACT_APP_SOCKET_PATH || '/socket.io',
        timeout: 10000,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
        withCredentials: true,
      });
      presenceSocketRef.current = s;
      s.on('connect_error', (err) => {
        try { console.warn('[Chat] presence connect_error:', err && (err.message || err)); } catch (_) {}
      });
      s.on('presence:list', (payload) => {
        const users = Array.isArray(payload?.users) ? payload.users.map(String) : [];
        setState(prev => {
          const friends = prev.friends.map(fr => ({ ...fr, online: users.includes(String(fr.id)) }));
          return { ...prev, friends };
        });
      });
      // Optionally mirror per-room list to safeguard if backend ever reuses event
      s.on('users:list', (payload) => {
        const users = Array.isArray(payload?.users) ? payload.users.map(String) : [];
        setState(prev => {
          const friends = prev.friends.map(fr => ({ ...fr, online: users.includes(String(fr.id)) }));
          return { ...prev, friends };
        });
      });
    } catch (_) {}
    return () => {
      try {
        const sock = presenceSocketRef.current;
        if (sock && sock.connected) sock.disconnect();
      } catch {}
      presenceSocketRef.current = null;
    };
  }, [chatServerUrl, currentUserId]);

    // Listen for global friends updates to create passive sockets and upsert friends
    useEffect(() => {
      const handler = (e) => {
        try {
          const list = e?.detail?.friends;
          if (!Array.isArray(list)) return;
          // Upsert friends into state
          setState(prev => {
            const existingIds = new Set(prev.friends.map(f => String(f.id)));
            const additions = list.filter(f => f && f.id && !existingIds.has(String(f.id))).map(f => ({ id: String(f.id), name: f.name || 'User', avatar: f.avatar || '', online: !!f.online }));
            if (additions.length === 0) return prev;
            return { ...prev, friends: [...prev.friends, ...additions] };
          });
          // Ensure sockets for each friend (without opening chat)
          list.forEach((f) => {
            const fid = String(f.id);
            if (!fid) return;
            if (!socketsRef.current[fid]) {
              const roomId = dmRoomId(currentUserId, fid);
              if (!roomId) return;
              const s = io(chatServerUrl, {
                transports: ['websocket', 'polling'],
                auth: { username: currentUserId, groupId: roomId },
                query: { groupId: roomId },
                path: process.env.REACT_APP_SOCKET_PATH || '/socket.io',
                timeout: 10000,
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 500,
                reconnectionDelayMax: 5000,
                randomizationFactor: 0.5,
                withCredentials: true,
              });
              socketsRef.current[fid] = s;
              s.on('connect_error', (err) => {
                try { console.warn(`[Chat] DM(passive) connect_error (friend=${fid}):`, err && (err.message || err)); } catch (_) {}
              });
              const onMsg = (msg) => {
                setState(prev => {
                  const chat = prev.chats[fid] || { messages: [], unread: 0, minimized: false, open: false };
                  // Ignore if room mismatch
                  if (chat.roomId && chat.roomId !== roomId) return prev;
                  // De-dup by id
                  const exists = (chat.messages || []).some(m0 => m0.id === msg.id);
                  if (exists) return prev;
                  const from = msg.username === currentUserId ? 'me' : 'friend';
                  const m = { id: msg.id, from, text: msg.text, ts: new Date(msg.timestamp).getTime() };
                  const isOpen = chat.open && !chat.minimized;
                  const unreadInc = isOpen ? 0 : (from === 'friend' ? 1 : 0);
                  return {
                    ...prev,
                    chats: { ...prev.chats, [fid]: { ...chat, roomId: roomId, messages: [...(chat.messages||[]), m], unread: (chat.unread||0) + unreadInc } },
                    notifDot: !isOpen ? true : prev.notifDot,
                  };
                });
              };
              const onHistory = (payload) => {
                const listH = Array.isArray(payload?.messages) ? payload.messages : [];
                setState(prev => {
                  const chat = prev.chats[fid] || {};
                  if (chat.roomId && chat.roomId !== roomId) return prev;
                  const mapped = listH.map(msg => ({ id: msg.id, from: msg.username === currentUserId ? 'me' : 'friend', text: msg.text, ts: new Date(msg.timestamp).getTime() }));
                  return { ...prev, chats: { ...prev.chats, [fid]: { ...chat, roomId: roomId, messages: mapped } } };
                });
              };
              const onTyping = (payload) => {
                const u = payload?.username;
                const isTyping = !!payload?.typing;
                if (!u || u === currentUserId) return;
                setState(prev => {
                  const chat = prev.chats[fid] || {};
                  if (chat.roomId && chat.roomId !== roomId) return prev;
                  return { ...prev, chats: { ...prev.chats, [fid]: { ...chat, roomId: roomId, typing: isTyping } } };
                });
              };
              s.on('chat:message', onMsg);
              s.on('chat:history', onHistory);
              s.on('user:typing', onTyping);
              // On connect/reconnect after backend restart, re-request history and merge from REST
              const refreshHistory = () => {
                try { s.emit('chat:history:request'); } catch (_) {}
                try {
                  fetchPersistedHistory({ userId: currentUserId, peerId: fid, limit: 100 }).then((dbItems) => {
                    if (!Array.isArray(dbItems) || dbItems.length === 0) return;
                    setState(prev => {
                      const chat = prev.chats[fid] || {};
                      if (chat.roomId && chat.roomId !== roomId) return prev;
                      const existing = Array.isArray(chat.messages) ? chat.messages : [];
                      const existingKeys = new Set(existing.map(m => `${m.from}|${m.text}|${m.ts}`));
                      const mapped = dbItems.map(it => ({ id: `db_${it._id}`, from: String(it.senderId) === String(currentUserId) ? 'me' : 'friend', text: (typeof it.content === 'string' ? it.content : (it.content && it.content.text) || ''), ts: new Date(it.sentAt || it.createdAt).getTime() }));
                      const merged = [...existing];
                      mapped.sort((a,b) => a.ts - b.ts).forEach(m => {
                        const key = `${m.from}|${m.text}|${m.ts}`;
                        if (!existingKeys.has(key)) merged.push(m);
                      });
                      return { ...prev, chats: { ...prev.chats, [fid]: { ...chat, roomId: roomId, messages: merged } } };
                    });
                  }).catch(() => {});
                } catch (_) {}
              };
              s.on('connect', refreshHistory);
              s.on('reconnect', refreshHistory);
              s.on('users:list', (payload) => {
                const users = Array.isArray(payload?.users) ? payload.users : [];
                setState(prev => {
                  const isOnline = users.includes(fid);
                  const friends = prev.friends.map(fr => String(fr.id) === fid ? { ...fr, online: isOnline } : fr);
                  return { ...prev, friends };
                });
              });
              // Global presence across namespace
              s.on('presence:list', (payload) => {
                const users = Array.isArray(payload?.users) ? payload.users.map(String) : [];
                setState(prev => {
                  const friends = prev.friends.map(fr => ({ ...fr, online: users.includes(String(fr.id)) }));
                  return { ...prev, friends };
                });
              });
              s.emit('chat:history:request');
            }
          });
        } catch (_) {}
      };
      try { window.addEventListener('chat:friendsUpdate', handler); } catch {}
      return () => { try { window.removeEventListener('chat:friendsUpdate', handler); } catch {} };
    }, [chatServerUrl, currentUserId]);

  const openChat = useCallback((friendId, friendData) => {
    const fid = String(friendId);
    setState((prev) => {
      const chat = prev.chats[fid] || { messages: [], unread: 0, minimized: false, open: false, typing: false };
      let friends = prev.friends;
      if (friendData && friendData.id) {
        const exists = prev.friends.some(f => String(f.id) === String(friendData.id));
        if (!exists) {
          friends = [...prev.friends, { id: String(friendData.id), name: friendData.name || 'User', avatar: friendData.avatar || '', online: !!friendData.online }];
        }
      }
      const roomId = dmRoomId(currentUserId, fid);
      const next = {
        ...prev,
        friends,
        chats: {
          ...prev.chats,
          [fid]: { ...chat, open: true, roomId }
        },
        openOrder: prev.openOrder.includes(fid) ? prev.openOrder : [...prev.openOrder, fid],
        notifDot: false,
      };
      return next;
    });

    // Ensure socket for this DM
    try {
      if (!socketsRef.current[fid]) {
        const roomId = dmRoomId(currentUserId, fid);
        const s = io(chatServerUrl, {
          transports: ['websocket', 'polling'],
          auth: { username: currentUserId, groupId: roomId },
          query: { groupId: roomId },
          path: process.env.REACT_APP_SOCKET_PATH || '/socket.io',
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 500,
          reconnectionDelayMax: 5000,
          randomizationFactor: 0.5,
          withCredentials: true,
        });
        socketsRef.current[fid] = s;
        s.on('connect_error', (err) => {
          try { console.warn(`[Chat] DM(open) connect_error (friend=${fid}):`, err && (err.message || err)); } catch (_) {}
        });
        const onMsg = (msg) => {
          setState(prev => {
            // Route by room: only accept messages for this room by checking current chat roomId
            const chat = prev.chats[fid] || { messages: [] };
            if (chat.roomId !== roomId) return prev;
            // Basic de-duplication by id
            const existing = (chat.messages || []).some(m0 => m0.id === msg.id);
            if (existing) return prev;
            const from = msg.username === currentUserId ? 'me' : 'friend';
            const m = { id: msg.id, from, text: msg.text, ts: new Date(msg.timestamp).getTime() };
            const isOpen = chat.open && !chat.minimized;
            const unreadInc = isOpen ? 0 : (from === 'friend' ? 1 : 0);
            return {
              ...prev,
              chats: { ...prev.chats, [fid]: { ...chat, messages: [...(chat.messages||[]), m], unread: (chat.unread||0) + unreadInc } },
              notifDot: !isOpen ? true : prev.notifDot,
            };
          });
        };
        const onHistory = (payload) => {
          const list = Array.isArray(payload?.messages) ? payload.messages : [];
          setState(prev => {
            const chat = prev.chats[fid] || {};
            if (chat.roomId !== roomId) return prev;
            const mapped = list.map(msg => ({ id: msg.id, from: msg.username === currentUserId ? 'me' : 'friend', text: msg.text, ts: new Date(msg.timestamp).getTime() }));
            return { ...prev, chats: { ...prev.chats, [fid]: { ...chat, messages: mapped } } };
          });
        };
        const onTyping = (payload) => {
          const u = payload?.username;
          const isTyping = !!payload?.typing;
          if (!u || u === currentUserId) return;
          setState(prev => {
            const chat = prev.chats[fid] || {};
            if (chat.roomId !== roomId) return prev;
            return { ...prev, chats: { ...prev.chats, [fid]: { ...chat, typing: isTyping } } };
          });
        };
        s.on('chat:message', onMsg);
        s.on('chat:history', onHistory);
        s.on('user:typing', onTyping);
        // On connect/reconnect after backend restart, re-request history and merge from REST
        const refreshHistory = () => {
          try { s.emit('chat:history:request'); } catch (_) {}
          try {
            fetchPersistedHistory({ userId: currentUserId, peerId: fid, limit: 100 }).then((dbItems) => {
              if (!Array.isArray(dbItems) || dbItems.length === 0) return;
              setState(prev => {
                const chat = prev.chats[fid] || {};
                if (chat.roomId !== roomId) return prev;
                const existing = Array.isArray(chat.messages) ? chat.messages : [];
                const existingKeys = new Set(existing.map(m => `${m.from}|${m.text}|${m.ts}`));
                const mapped = dbItems.map(it => ({ id: `db_${it._id}`, from: String(it.senderId) === String(currentUserId) ? 'me' : 'friend', text: (typeof it.content === 'string' ? it.content : (it.content && it.content.text) || ''), ts: new Date(it.sentAt || it.createdAt).getTime() }));
                const merged = [...existing];
                mapped.sort((a,b) => a.ts - b.ts).forEach(m => {
                  const key = `${m.from}|${m.text}|${m.ts}`;
                  if (!existingKeys.has(key)) merged.push(m);
                });
                return { ...prev, chats: { ...prev.chats, [fid]: { ...chat, messages: merged } } };
              });
            }).catch(() => {});
          } catch (_) {}
        };
        s.on('connect', refreshHistory);
        s.on('reconnect', refreshHistory);
        s.on('users:list', (payload) => {
          const list = Array.isArray(payload?.users) ? payload.users : [];
          setState(prev => {
            const chat = prev.chats[fid] || {};
            if (chat.roomId !== roomId) return prev;
            const isOnline = list.includes(fid);
            // update friend.online flag
            const friends = prev.friends.map(fr => String(fr.id) === fid ? { ...fr, online: isOnline } : fr);
            return { ...prev, friends };
          });
        });
        // Global presence across namespace
        s.on('presence:list', (payload) => {
          const users = Array.isArray(payload?.users) ? payload.users.map(String) : [];
          setState(prev => {
            const friends = prev.friends.map(fr => ({ ...fr, online: users.includes(String(fr.id)) }));
            return { ...prev, friends };
          });
        });
        s.emit('chat:history:request');
        // Also fetch persisted history from REST and merge
        try {
          fetchPersistedHistory({ userId: currentUserId, peerId: fid, limit: 100 }).then((dbItems) => {
            if (!Array.isArray(dbItems) || dbItems.length === 0) return;
            setState(prev => {
              const chat = prev.chats[fid] || {};
              if (chat.roomId !== roomId) return prev;
              const existing = Array.isArray(chat.messages) ? chat.messages : [];
              const existingKeys = new Set(existing.map(m => `${m.from}|${m.text}|${m.ts}`));
              const mapped = dbItems.map(it => ({ id: `db_${it._id}`, from: String(it.senderId) === String(currentUserId) ? 'me' : 'friend', text: (typeof it.content === 'string' ? it.content : (it.content && it.content.text) || ''), ts: new Date(it.sentAt || it.createdAt).getTime() }));
              const merged = [...existing];
              mapped.sort((a,b) => a.ts - b.ts).forEach(m => {
                const key = `${m.from}|${m.text}|${m.ts}`;
                if (!existingKeys.has(key)) merged.push(m);
              });
              return { ...prev, chats: { ...prev.chats, [fid]: { ...chat, messages: merged } } };
            });
          }).catch(() => {});
        } catch (_) {}
        s.on('disconnect', () => {
          // optional: handle reconnects automatically by socket.io
        });
      }
    } catch (_) {}
  }, [chatServerUrl, currentUserId]);

  // When user changes (re-login), reset sockets and refresh open chats' history
  const prevUserRef = useRef(currentUserId);
  useEffect(() => {
    if (prevUserRef.current === currentUserId) return;
    // Disconnect presence socket
    try {
      const ps = presenceSocketRef.current;
      if (ps && ps.connected) ps.disconnect();
    } catch {}
    presenceSocketRef.current = null;
    // Disconnect all DM sockets
    try {
      const map = socketsRef.current || {};
      Object.keys(map).forEach((k) => { try { const s = map[k]; if (s && s.connected) s.disconnect(); } catch {} });
    } catch {}
    socketsRef.current = {};
    // Reset chats and collect opened chats to re-open
    const opened = [];
    setState(prev => {
      const nextChats = {};
      Object.entries(prev.chats || {}).forEach(([fid, chat]) => {
        if (chat?.open) opened.push(String(fid));
        nextChats[fid] = { ...chat, roomId: dmRoomId(currentUserId, String(fid)), messages: [] };
      });
      return { ...prev, chats: nextChats };
    });
    // Re-open chats to trigger history requests under new identity
    setTimeout(() => {
      opened.forEach((fid) => { try { openChat(fid); } catch {} });
    }, 0);
    prevUserRef.current = currentUserId;
  }, [currentUserId, openChat]);

  const closeChat = useCallback((friendId) => {
    setState((prev) => {
      const chat = prev.chats[friendId];
      if (!chat) return prev;
      const next = {
        ...prev,
        chats: { ...prev.chats, [friendId]: { ...chat, open: false } },
        openOrder: prev.openOrder.filter(id => id !== friendId),
      };
      return next;
    });
  }, []);

  const minimizeChat = useCallback((friendId) => {
    setState((prev) => {
      const chat = prev.chats[friendId];
      const next = {
        ...prev,
        chats: { ...prev.chats, [friendId]: { ...(chat || { messages: [], unread: 0 }), minimized: true, open: true } },
      };
      return next;
    });
  }, []);

  const restoreChat = useCallback((friendId) => {
    setState((prev) => {
      const chat = prev.chats[friendId];
      const next = {
        ...prev,
        chats: { ...prev.chats, [friendId]: { ...(chat || { messages: [], unread: 0 }), minimized: false, open: true, unread: 0 } },
      };
      return next;
    });
  }, []);

  const sendMessage = useCallback((friendId, text) => {
    const fid = String(friendId);
    // Do NOT optimistically append here to avoid duplicate when server echoes the same message back
    // Just ensure the chat is open and let onMsg handle appending using server-generated id
    setState((prev) => {
      const chat = prev.chats[fid] || { messages: [], unread: 0, minimized: false, open: false, typing: false };
      return {
        ...prev,
        chats: {
          ...prev.chats,
          [fid]: {
            ...chat,
            open: true,
          }
        }
      };
    });
    try {
      const s = socketsRef.current[fid];
      const roomId = dmRoomId(currentUserId, fid);
      if (s && roomId) {
        s.emit('chat:message', { text }, () => {});
        // Fire-and-forget persist via REST to ensure history after relogin
        try { persistMessage({ receiverId: fid, text }); } catch (_) {}
        // Emit typing false immediately
        try { s.emit('typing', { typing: false }); } catch (_e) {}
      } else {
        // If socket not ready, still persist so message is not lost
        try { persistMessage({ receiverId: fid, text }); } catch (_) {}
      }
    } catch (_) {}
  }, [currentUserId]);

  // keep for possible local injections, but server drives receive
  const receiveMessage = useCallback((friendId, text) => {
    const fid = String(friendId);
    setState((prev) => {
      const chat = prev.chats[fid] || { messages: [], unread: 0, minimized: false, open: false, typing: false };
      const msg = { id: `m_${Date.now()}`, from: 'friend', text, ts: Date.now() };
      const isOpen = chat.open && !chat.minimized;
      const unreadInc = isOpen ? 0 : 1;
      return {
        ...prev,
        chats: {
          ...prev.chats,
          [fid]: {
            ...chat,
            messages: [...(chat.messages||[]), msg],
            unread: (chat.unread || 0) + unreadInc,
          }
        },
        notifDot: !isOpen || !chat.open ? true : prev.notifDot,
      };
    });
  }, []);

  const markRead = useCallback((friendId) => {
    const fid = String(friendId);
    setState(prev => ({ ...prev, chats: { ...prev.chats, [fid]: { ...(prev.chats[fid]||{}), unread: 0 } }, notifDot: false }));
  }, []);

  const emitTyping = useCallback((friendId, typing) => {
    try {
      const fid = String(friendId);
      const s = socketsRef.current[fid];
      if (s) s.emit('typing', { typing: !!typing });
    } catch (_) {}
  }, []);

  const value = useMemo(() => ({
    state,
    openChat,
    closeChat,
    minimizeChat,
    restoreChat,
    sendMessage,
    markRead,
    emitTyping,
  }), [state, openChat, closeChat, minimizeChat, restoreChat, sendMessage, markRead, emitTyping]);

  return (
    <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
  );
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
}
